import { Controller } from "@hotwired/stimulus"

// Drives one on-page signature (or initials) capture on /sign. The signer draws
// with mouse OR touch on a <canvas>; the drawing is exported as a base64 PNG into
// a hidden field the form posts. A "type in cursive" fallback renders a typed
// name/initials onto the same canvas. A Clear button wipes it. Blank-canvas
// detection lives on BOTH the client (this.hasSignature) and the server
// (Esign::SignatureImage) — this is the courtesy guard; the server is authority.
//
// Modes:
//   signature (default) — full signature; cursive source is the typed legal name.
//   initials            — a compact initials field; cursive source is the typed
//                         initials input.
//
// Progressive enhancement: the form still POSTs without JS; the server rejects a
// missing/blank signature with an inline error, so nothing silently succeeds.
export default class extends Controller {
  static targets = [
    "canvas", "input", "methodInput", "typedSource", "clearButton", "hint"
  ]
  static values = { mode: { type: String, default: "signature" } }

  connect() {
    this.hasSignature = false
    this.drawing = false

    // Typed-only mode (e.g. inline initials) has no canvas — nothing to wire up.
    if (!this.hasCanvasTarget) return

    this.ctx = this.canvasTarget.getContext("2d")
    this.setupHiDPI()
    this.strokeStyle()

    // Pointer events cover mouse + touch + pen in one API.
    this.canvasTarget.addEventListener("pointerdown", this.start)
    this.canvasTarget.addEventListener("pointermove", this.move)
    window.addEventListener("pointerup", this.end)
    // Stop touch-drag from scrolling the page while signing.
    this.canvasTarget.addEventListener("touchstart", this.preventTouch, { passive: false })
    this.canvasTarget.addEventListener("touchmove", this.preventTouch, { passive: false })

    // Export on form submit as a final safety net (in case drawing ended oddly).
    // Only the main signature canvas guards the submit; inline initials are
    // validated server-side (and are typed-only here, so there's no canvas).
    this.form = this.element.closest("form")
    if (this.form) this.form.addEventListener("submit", this.onSubmit)
  }

  disconnect() {
    if (!this.hasCanvasTarget) return
    this.canvasTarget.removeEventListener("pointerdown", this.start)
    this.canvasTarget.removeEventListener("pointermove", this.move)
    window.removeEventListener("pointerup", this.end)
    this.canvasTarget.removeEventListener("touchstart", this.preventTouch)
    this.canvasTarget.removeEventListener("touchmove", this.preventTouch)
    if (this.form) this.form.removeEventListener("submit", this.onSubmit)
  }

  // Scale the backing store for crisp lines on retina screens without changing
  // the CSS size, so the exported PNG isn't blurry or oversized.
  setupHiDPI() {
    const ratio = window.devicePixelRatio || 1
    const rect = this.canvasTarget.getBoundingClientRect()
    this.canvasTarget.width = Math.max(1, Math.round(rect.width * ratio))
    this.canvasTarget.height = Math.max(1, Math.round(rect.height * ratio))
    this.ctx.scale(ratio, ratio)
    this.cssWidth = rect.width
    this.cssHeight = rect.height
  }

  strokeStyle() {
    this.ctx.lineWidth = 2
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"
    this.ctx.strokeStyle = "#111827"
  }

  point(event) {
    const rect = this.canvasTarget.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  start = (event) => {
    this.drawing = true
    const p = this.point(event)
    this.ctx.beginPath()
    this.ctx.moveTo(p.x, p.y)
    event.preventDefault()
  }

  move = (event) => {
    if (!this.drawing) return
    const p = this.point(event)
    this.ctx.lineTo(p.x, p.y)
    this.ctx.stroke()
    this.hasSignature = true
    event.preventDefault()
  }

  end = () => {
    if (!this.drawing) return
    this.drawing = false
    if (this.hasSignature) this.capture("drawn")
  }

  preventTouch = (event) => { event.preventDefault() }

  // Render the typed source (legal name / initials) onto the canvas in cursive.
  typeInCursive() {
    const text = this.hasTypedSourceTarget ? (this.typedSourceTarget.value || "").trim() : ""
    if (!text) {
      this.showHint("Type your name first, then tap “Type in cursive”.")
      return
    }
    this.ctx.clearRect(0, 0, this.cssWidth, this.cssHeight)
    const size = this.modeValue === "initials" ? 28 : 40
    this.ctx.fillStyle = "#111827"
    this.ctx.font = `italic ${size}px "Brush Script MT", "Segoe Script", cursive`
    this.ctx.textBaseline = "middle"
    this.ctx.fillText(text, 12, this.cssHeight / 2)
    this.hasSignature = true
    this.capture("typed")
    this.hideHint()
  }

  clear() {
    this.ctx.clearRect(0, 0, this.cssWidth, this.cssHeight)
    this.hasSignature = false
    this.inputTarget.value = ""
    if (this.hasMethodInputTarget) this.methodInputTarget.value = ""
  }

  // Write the current canvas into the hidden field + record the method.
  capture(method) {
    this.inputTarget.value = this.canvasTarget.toDataURL("image/png")
    if (this.hasMethodInputTarget) this.methodInputTarget.value = method
  }

  onSubmit = (event) => {
    if (this.hasSignature) {
      // Ensure the very latest strokes are captured.
      if (!this.inputTarget.value) this.capture(this.currentMethod())
      return
    }
    // Client courtesy guard — block the submit and prompt. The server also
    // rejects, so a no-JS submit still fails safe.
    event.preventDefault()
    this.showHint(
      this.modeValue === "initials"
        ? "Please add your initials here."
        : "Please sign in the box above."
    )
    this.canvasTarget.focus()
  }

  currentMethod() {
    return this.hasMethodInputTarget && this.methodInputTarget.value
      ? this.methodInputTarget.value
      : "drawn"
  }

  showHint(message) {
    if (!this.hasHintTarget) return
    this.hintTarget.textContent = message
    this.hintTarget.classList.remove("hidden")
  }

  hideHint() {
    if (this.hasHintTarget) this.hintTarget.classList.add("hidden")
  }
}
